import React from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import {
  currentCaseStateLabel,
  safetyCaseOutcomeStateLabel,
  safetyCaseReviewStateLabel,
} from '../../domain/safetyCases/safetyCaseWorklist';
import { liveSafetyCaseDisplayTitle } from '../../domain/safetyCases/safetyScanWorkspaceHandoff';

type Props = {
  linkedSafetyCases: SafetyCaseRecord[];
  loading?: boolean;
  onSelectSafetyCase: () => void;
  onBackToSafetyCases: () => void;
  onOpenSafetyCase: (item: SafetyCaseRecord) => void;
};

function titleCase(value: unknown): string {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function readAny(item: SafetyCaseRecord, paths: string[][]): string {
  for (const path of paths) {
    let current: any = item;
    for (const key of path) {
      current = current?.[key];
    }
    const value = String(current || '').trim();
    if (value) return value;
  }
  return '';
}

function riskPatternLabel(item: SafetyCaseRecord): string {
  return readAny(item, [
    ['risk_pattern_label'],
    ['unsafe_condition_title'],
    ['scope', 'risk_pattern_label'],
    ['scope', 'risk_label'],
    ['scope', 'policy_label'],
    ['scope', 'risk_type'],
    ['hazard_category'],
    ['finding_type'],
  ]) || 'Safety Case';
}

function safetyCaseTitle(item: SafetyCaseRecord): string {
  return liveSafetyCaseDisplayTitle(item) || `Safety Case: ${riskPatternLabel(item)}`;
}

function cameraLabel(item: SafetyCaseRecord): string {
  return readAny(item, [
    ['camera_label'],
    ['scope', 'camera_label'],
    ['scope', 'camera_id'],
    ['status_snapshot', 'source_context', 'camera_label'],
    ['status_snapshot', 'source_context', 'camera_id'],
  ]);
}

function zoneLabel(item: SafetyCaseRecord): string {
  return readAny(item, [
    ['zone_label'],
    ['area_label'],
    ['scope', 'zone_label'],
    ['scope', 'zone_id'],
    ['status_snapshot', 'source_context', 'zone_label'],
    ['status_snapshot', 'source_context', 'zone_id'],
  ]);
}

function latestActivity(item: SafetyCaseRecord): string {
  return readAny(item, [
    ['source_workflow_updated_at'],
    ['updated_at'],
    ['status_snapshot', 'source_context', 'latest_activity_at'],
  ]);
}

export default function LinkedSafetyCasesPanel({
  linkedSafetyCases,
  loading = false,
  onSelectSafetyCase,
  onBackToSafetyCases,
  onOpenSafetyCase,
}: Props) {
  const hasLinkedSafetyCases = linkedSafetyCases.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <Stack spacing={1.25}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Linked Safety Case
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Linked Safety Case is context for this measurement window. This Safety Scan is not a Safety Case.
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading linked Safety Cases...
          </Typography>
        ) : !hasLinkedSafetyCases ? (
          <>
            <Alert severity="warning">
              No Safety Case is linked to this Draft Safety Scan yet.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              A Safety Scan packages one or more Safety Cases for sign-off and sharing. Select a Safety Case before building or signing the scan.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
              <Button variant="contained" onClick={onSelectSafetyCase}>
                Select Safety Case
              </Button>
              <Button variant="outlined" onClick={onBackToSafetyCases}>
                Back to Safety Cases
              </Button>
            </Stack>
          </>
        ) : (
          <Stack spacing={1}>
            {linkedSafetyCases.map((item) => (
              <Box
                key={String(item.safety_case_id || item.finding_id || item.workflow_id)}
                sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 1.5 }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {safetyCaseTitle(item)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{`Originating Risk Pattern: ${riskPatternLabel(item)}`}</Typography>
                    <Typography variant="body2" color="text.secondary">{`Hazard Category: ${titleCase(item.hazard_category || 'Not recorded')}`}</Typography>
                    {zoneLabel(item) ? (
                      <Typography variant="body2" color="text.secondary">{`Area / Zone: ${zoneLabel(item)}`}</Typography>
                    ) : null}
                    {cameraLabel(item) ? (
                      <Typography variant="body2" color="text.secondary">{`Camera: ${cameraLabel(item)}`}</Typography>
                    ) : null}
                    <Typography variant="body2" color="text.secondary">
                      {`Safety Case state: ${currentCaseStateLabel(item)}`}
                    </Typography>
                    {safetyCaseReviewStateLabel(item) ? (
                      <Typography variant="body2" color="text.secondary">{`Review state: ${safetyCaseReviewStateLabel(item)}`}</Typography>
                    ) : null}
                    {safetyCaseOutcomeStateLabel(item) ? (
                      <Typography variant="body2" color="text.secondary">{`Outcome state: ${safetyCaseOutcomeStateLabel(item)}`}</Typography>
                    ) : null}
                    {latestActivity(item) ? (
                      <Typography variant="body2" color="text.secondary">{`Latest activity: ${latestActivity(item)}`}</Typography>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={`${item.recommendations?.length || 0} recommendations`} />
                    <Chip size="small" variant="outlined" label={`${item.outcome_learning_summary?.validated_count || 0} validated`} />
                    <Button size="small" variant="outlined" onClick={() => onOpenSafetyCase(item)}>
                      Open Safety Case
                    </Button>
                  </Stack>
                </Stack>
                {(item.outcome_learning_summary?.highlights || [])[0] ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {`Latest learning: ${(item.outcome_learning_summary?.highlights || [])[0]}`}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
